import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LOGO_ABSOLUTE } from "@/lib/brand";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  ogImage?: string;
  url?: string;
  canonical?: string;
  type?: string;
}

export function SEO({
  title = "DevDotCom - Engineering Excellence for Modern Teams",
  description = "Award-winning engineering team delivering scalable solutions. From EV platforms to government services. SIH 2025 Winners.",
  keywords = "DevDotCom, software engineering, web development, cloud infrastructure, tech solutions India",
  image = LOGO_ABSOLUTE,
  ogImage,
  url,
  canonical,
  type = "website",
}: SEOProps) {
  const location = useLocation();
  const currentUrl = url || `https://devdotcom.org${location.pathname}`;
  const canonicalUrl = canonical
    ? `https://devdotcom.org${canonical}`
    : currentUrl;
  // Always emit an absolute OG image URL (crawlers reject relative paths)
  const ogImageUrl = (() => {
    const raw = ogImage || image;
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
    return `https://devdotcom.org${raw.startsWith("/") ? raw : `/${raw}`}`;
  })();

  useEffect(() => {
    // Update document title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (
      property: string,
      content: string,
      isProperty = false,
    ) => {
      const attribute = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attribute}="${property}"]`);

      if (element) {
        element.setAttribute("content", content);
      } else {
        element = document.createElement("meta");
        element.setAttribute(attribute, property);
        element.setAttribute("content", content);
        document.head.appendChild(element);
      }
    };

    // Standard meta tags
    updateMetaTag("description", description);
    updateMetaTag("keywords", keywords);

    // Open Graph tags
    updateMetaTag("og:title", title, true);
    updateMetaTag("og:description", description, true);
    updateMetaTag("og:image", ogImageUrl, true);
    updateMetaTag("og:url", currentUrl, true);
    updateMetaTag("og:type", type, true);

    // Twitter Card tags
    updateMetaTag("twitter:title", title);
    updateMetaTag("twitter:description", description);
    updateMetaTag("twitter:image", ogImageUrl);
    updateMetaTag("twitter:card", "summary_large_image");

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute("href", canonicalUrl);
    } else {
      canonicalLink = document.createElement("link");
      canonicalLink.setAttribute("rel", "canonical");
      canonicalLink.setAttribute("href", canonicalUrl);
      document.head.appendChild(canonicalLink);
    }
  }, [
    title,
    description,
    keywords,
    ogImageUrl,
    currentUrl,
    canonicalUrl,
    type,
  ]);

  return null;
}

// Page-specific SEO configurations
export const seoConfig = {
  home: {
    title:
      "DevDotCom - Engineering Excellence for Modern Teams | Award-Winning Solutions",
    description:
      "DevDotCom builds long-term technology partnerships with engineering-first solutions. From EV platforms to government digital services. SIH 2025 Winners | Smart India Hackathon Champions.",
    keywords:
      "DevDotCom, engineering excellence, software development, EV platform, government tech, SIH winners, web development India, cloud solutions, API integration",
  },
  about: {
    title: "About DevDotCom - Award-Winning Engineering Team | Our Story",
    description:
      "Meet the award-winning team behind DevDotCom. SIH 2025 Winners, Hackathon Champions, building scalable technology solutions for modern teams. Learn about our engineering-first approach.",
    keywords:
      "DevDotCom team, about devdotcom, SIH 2025 winners, engineering team India, tech company story, award-winning developers",
  },
  contact: {
    title: "Contact DevDotCom - Let's Build Together",
    description:
      "Get in touch with DevDotCom. Tell us about your product, timeline, and goals - we reply within one business day with a clear next step.",
    keywords:
      "contact DevDotCom, hire software engineers, software development inquiry, DevDotCom email, tech partnership",
  },
  services: {
    title: "Services - Web Development, Cloud & Mobile Solutions | DevDotCom",
    description:
      "Professional software engineering services: Web Development, Cloud Infrastructure, Mobile Apps, API Integration. Build with a team that values quality and long-term partnerships.",
    keywords:
      "web development services, cloud infrastructure, mobile app development, API integration, software consulting, enterprise solutions",
  },
  products: {
    title: "Products - DevCLI, DataSync, SecureAuth | DevDotCom",
    description:
      "Explore DevDotCom's suite of developer tools and products. DevCLI, DataSync, SecureAuth, and FlowBuilder - engineered for teams that ship fast.",
    keywords:
      "developer tools, DevCLI, DataSync, SecureAuth, FlowBuilder, dev products, engineering tools",
  },
};
